// Public lead-capture form rendered on ebook articles.
// Captures email + GDPR consent + optional newsletter signup, then triggers
// the PDF download AND sends a Resend confirmation with the same link.
import { useState } from "react";
import { Download, Loader2, BookOpen, Check, ShieldCheck } from "lucide-react";
import { useT } from "@/lib/i18n";
import { apiFetch, API } from "@/lib/api";

const formatBytes = (n) => {
  if (!n || n <= 0) return "";
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const EbookDownloadForm = ({ slug, title, lang, pages, sizeBytes, filename }) => {
  const t = useT();
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [newsletter, setNewsletter] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);  // { download_url }

  const consentText = t("ebookF.consent_text");

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { setError(t("ebookF.error_consent")); return; }
    if (!email || !email.includes("@")) { setError(t("ebookF.error_email")); return; }
    setError(null); setSubmitting(true);
    const { ok, data: d, error: err } = await apiFetch(`/api/blog/ebooks/${encodeURIComponent(slug)}/download`, {
      method: "POST",
      body: JSON.stringify({
        email: email.trim(),
        lang,
        consent: true,
        consent_text: consentText,
        newsletter_optin: newsletter,
        source: `ebook:${slug}`,
      }),
    });
    if (!ok) {
      setError(err || "Une erreur est survenue");
      setSubmitting(false);
      return;
    }
    setSuccess({ download_url: `${API}${d.download_url}` });
    // Trigger immediate download in a new tab — works on all browsers and
    // lets the user keep the success state visible on the original page.
    try { window.open(`${API}${d.download_url}`, "_blank", "noopener,noreferrer"); } catch { /* ignore */ }
    setSubmitting(false);
  };

  if (success) {
    return (
      <div className="bg-white border-2 border-[color:var(--gv-pink)] rounded-[8px] p-6 lg:p-8" data-testid="ebook-form-success">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
            <Check size={20} className="text-emerald-600" />
          </div>
          <div>
            <div className="font-display text-xl text-[color:var(--gv-ink)]">{t("ebookF.success_title")}</div>
            <div className="text-[13px] text-[color:var(--gv-ink)]/65">{t("ebookF.success_email_sent")}</div>
          </div>
        </div>
        <p className="text-[14px] text-[color:var(--gv-ink)]/75 mb-5">{t("ebookF.success_msg")}</p>
        <a
          href={success.download_url}
          target="_blank"
          rel="noreferrer"
          className="btn-primary inline-flex items-center gap-2"
          data-testid="ebook-success-download"
        >
          <Download size={14} /> {t("ebookF.download_btn")}
        </a>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="bg-white border-2 border-[color:var(--gv-pink)]/40 rounded-[8px] p-6 lg:p-8 shadow-sm"
      data-testid="ebook-download-form"
    >
      <div className="flex items-start gap-3 mb-5">
        <div className="w-11 h-11 rounded-[6px] bg-[color:var(--gv-pink)]/10 flex items-center justify-center shrink-0">
          <BookOpen size={22} className="text-[color:var(--gv-pink)]" />
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-1">{t("ebookF.eyebrow")}</div>
          <div className="font-display text-xl lg:text-2xl text-[color:var(--gv-ink)] leading-tight">{t("ebookF.title")}</div>
          {(pages > 0 || sizeBytes > 0) && (
            <div className="text-[12px] text-[color:var(--gv-ink)]/55 mt-1">
              {pages > 0 && <>{pages} {t("ebookF.pages_label")}</>}
              {pages > 0 && sizeBytes > 0 && " · "}
              {sizeBytes > 0 && <>PDF · {formatBytes(sizeBytes)}</>}
            </div>
          )}
        </div>
      </div>

      <p className="text-[14px] text-[color:var(--gv-ink)]/75 leading-relaxed mb-4">{t("ebookF.intro")}</p>

      <label className="block text-[12.5px] text-[color:var(--gv-ink)] font-medium mb-1.5" htmlFor={`ebook-email-${slug}`}>
        {t("ebookF.email_label")}
      </label>
      <input
        id={`ebook-email-${slug}`}
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t("ebookF.email_placeholder")}
        className="w-full h-11 px-3 bg-white border border-[color:var(--gv-line)] rounded-[5px] text-[14px] focus:border-[color:var(--gv-pink)]/60 outline-none mb-4"
        data-testid="ebook-form-email"
      />

      <label className="flex items-start gap-2 text-[12.5px] text-[color:var(--gv-ink)]/80 mb-3 cursor-pointer">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          required
          className="mt-0.5 accent-[color:var(--gv-pink)]"
          data-testid="ebook-form-consent"
        />
        <span>{consentText}</span>
      </label>

      <label className="flex items-start gap-2 text-[12.5px] text-[color:var(--gv-ink)]/80 mb-5 cursor-pointer">
        <input
          type="checkbox"
          checked={newsletter}
          onChange={(e) => setNewsletter(e.target.checked)}
          className="mt-0.5 accent-[color:var(--gv-pink)]"
          data-testid="ebook-form-newsletter"
        />
        <span>{t("ebookF.newsletter_label")}</span>
      </label>

      {error && (
        <div className="text-[12.5px] text-red-600 bg-red-50 border border-red-200 rounded-[5px] p-2.5 mb-3" data-testid="ebook-form-error">{error}</div>
      )}

      <button
        type="submit"
        disabled={submitting || !email || !consent}
        className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="ebook-form-submit"
      >
        {submitting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
        {submitting ? t("ebookF.submitting") : t("ebookF.submit")}
      </button>

      <div className="flex items-center justify-center gap-1.5 text-[11px] text-[color:var(--gv-ink)]/55 mt-3">
        <ShieldCheck size={11} /> {t("ebookF.gdpr_hint")}
      </div>
    </form>
  );
};

export default EbookDownloadForm;
